import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Menu, X, User } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChatWidget } from "@/components/ChatWidget";
import { EnquiryDialog } from "@/components/EnquiryDialog";

// Clean Kin Logo Component - Dynamic sizing with brand image
export const CleanKinLogo = ({ size = "default" }) => {
  const sizes = {
    small: { width: "w-12", height: "h-12" },
    default: { width: "w-14 md:w-16", height: "h-14 md:h-16" },
    large: { width: "w-24 md:w-28", height: "h-24 md:h-28" }
  };
  const s = sizes[size];
  
  return (
    <div className="flex items-center transition-transform hover:scale-105 duration-300">
      <img 
        src="/logo-header.png" 
        alt="Clean Kin - Cleaning Services" 
        className={`${s.width} ${s.height} object-contain drop-shadow-sm`}
      />
    </div>
  );
};

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Auth lives in httpOnly cookie; we use a lightweight UI flag in localStorage
    const flag = localStorage.getItem("customer_logged_in");
    setIsLoggedIn(flag === "true");
  }, [location]);

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50">
      <nav className="container-clean">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <CleanKinLogo size="default" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className="text-[#1A2E12] hover:text-[#2D5016] font-medium transition-colors"
              data-testid="nav-home"
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className="text-[#1A2E12] hover:text-[#2D5016] font-medium transition-colors"
              data-testid="nav-about"
            >
              About
            </Link>
            <Link 
              to="/services" 
              className="text-[#1A2E12] hover:text-[#2D5016] font-medium transition-colors"
              data-testid="nav-services"
            >
              Services
            </Link>
            <Link 
              to="/offers" 
              className="text-[#1A2E12] hover:text-[#2D5016] font-medium transition-colors"
              data-testid="nav-offers"
            >
              Offers
            </Link>
            <Link 
              to="/careers" 
              className="text-[#1A2E12] hover:text-[#2D5016] font-medium transition-colors"
              data-testid="nav-careers"
            >
              Careers
            </Link>
            <Link 
              to="/booking" 
              className="text-[#1A2E12] hover:text-[#2D5016] font-medium transition-colors"
              data-testid="nav-booking"
            >
              Book Now
            </Link>
            <EnquiryDialog source="header" subject="General enquiry">
              <button
                type="button"
                className="flex items-center gap-2 text-[#2D5016] font-semibold"
                data-testid="nav-email"
                aria-label="Email Clean Kin"
              >
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                <span>Email Us</span>
              </button>
            </EnquiryDialog>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <Link to="/account">
                <Button 
                  variant="outline"
                  className="border-[#2D5016] text-[#2D5016] hover:bg-[#E8F5E9] rounded-full px-5 py-5"
                  data-testid="nav-account"
                >
                  <User className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  My Account
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button 
                  variant="ghost"
                  className="text-[#2D5016] hover:bg-[#E8F5E9] rounded-full px-5 py-5"
                  data-testid="nav-login"
                >
                  Sign In
                </Button>
              </Link>
            )}
            <Link to="/booking">
              <Button 
                className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-6 py-5 btn-primary"
                data-testid="nav-cta"
              >
                Get a Free Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#E2E8E0] animate-fadeIn">
            <div className="flex flex-col gap-4">
              <Link 
                to="/" 
                className="text-[#1A2E12] hover:text-[#2D5016] font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/about" 
                className="text-[#1A2E12] hover:text-[#2D5016] font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
              <Link 
                to="/services" 
                className="text-[#1A2E12] hover:text-[#2D5016] font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Services
              </Link>
              <Link 
                to="/offers" 
                className="text-[#1A2E12] hover:text-[#2D5016] font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Offers
              </Link>
              <Link 
                to="/careers" 
                className="text-[#1A2E12] hover:text-[#2D5016] font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Careers
              </Link>
              <Link 
                to="/booking" 
                className="text-[#1A2E12] hover:text-[#2D5016] font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Book Now
              </Link>
              {isLoggedIn ? (
                <Link 
                  to="/account" 
                  className="text-[#2D5016] font-semibold py-2 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User className="w-4 h-4" strokeWidth={1.5} />
                  My Account
                </Link>
              ) : (
                <Link 
                  to="/login" 
                  className="text-[#2D5016] font-semibold py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
              )}
              <EnquiryDialog source="header-mobile" subject="General enquiry">
                <button
                  type="button"
                  className="flex items-center gap-2 text-[#2D5016] font-semibold py-2"
                  aria-label="Email Clean Kin"
                  data-testid="nav-email-mobile"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                  <span>Email Us</span>
                </button>
              </EnquiryDialog>
              <Link to="/booking" onClick={() => setMobileMenuOpen(false)}>
                <Button className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full w-full py-5">
                  Get a Free Quote
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="footer-gradient border-t border-[#E2E8E0] mt-24">
      <div className="container-clean py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <CleanKinLogo size="default" />
            <p className="text-[#5C6B57] leading-relaxed max-w-md mt-4">
              A London &amp; Birmingham cleaning service. Professional, vetted cleaners trained to deliver a consistent standard.
            </p>
            <div className="mt-4 flex items-center gap-2 text-[#2D5016] text-sm font-medium">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              Vetted Cleaners
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-[#1A2E12] mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-[#5C6B57] hover:text-[#2D5016] transition-colors">About</Link></li>
              <li><Link to="/services" className="text-[#5C6B57] hover:text-[#2D5016] transition-colors">Services</Link></li>
              <li><Link to="/offers" className="text-[#5C6B57] hover:text-[#2D5016] transition-colors">Offers &amp; Subscriptions</Link></li>
              <li><Link to="/booking" className="text-[#5C6B57] hover:text-[#2D5016] transition-colors">Book a Clean</Link></li>
              <li><Link to="/careers" className="text-[#5C6B57] hover:text-[#2D5016] transition-colors">Careers</Link></li>
              <li><Link to="/legal" className="text-[#5C6B57] hover:text-[#2D5016] transition-colors">Legal &amp; Policies</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-[#1A2E12] mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-[#5C6B57]">
                <Mail className="w-4 h-4 text-[#7CB342]" strokeWidth={1.5} />
                <EnquiryDialog source="footer" subject="General enquiry">
                  <button
                    type="button"
                    className="hover:text-[#2D5016] text-left"
                    aria-label="Email Clean Kin"
                    data-testid="footer-email-link"
                  >
                    Email Us
                  </button>
                </EnquiryDialog>
              </li>
              <li className="flex items-start gap-2 text-[#5C6B57]">
                <MapPin className="w-4 h-4 text-[#7CB342] mt-1" strokeWidth={1.5} />
                <span>London &amp; Birmingham — contact us for other locations</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-[#E2E8E0] mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-[#5C6B57]">
              © {new Date().getFullYear()} Clean Kin Cleaning Services. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/legal#terms" className="text-[#5C6B57] hover:text-[#2D5016]">Terms</Link>
              <Link to="/legal#privacy" className="text-[#5C6B57] hover:text-[#2D5016]">Privacy</Link>
              <Link to="/legal#complaints" className="text-[#5C6B57] hover:text-[#2D5016]">Complaints</Link>
              <Link to="/legal#subscription" className="text-[#5C6B57] hover:text-[#2D5016]">Subscriptions</Link>
              <Link to="/team" className="text-[#5C6B57]/50 hover:text-[#2D5016]">Team</Link>
              <Link to="/admin" className="text-[#5C6B57] hover:text-[#2D5016]">Admin</Link>
            </div>
          </div>
          <p
            className="text-xs text-[#5C6B57]/70 mt-4 text-center md:text-left"
            data-testid="ai-disclaimer-footer"
          >
            <strong>AI disclaimer:</strong> Some written content and imagery on this site is AI-generated or AI-assisted for illustrative purposes. Service descriptions, pricing and policies are authoritative. See <Link to="/legal#ai-disclaimer" className="underline">full disclaimer</Link>. Clean Kin Cleaning Services is a trading name. Registered in England &amp; Wales.
          </p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20">
        {children}
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
};
